import { ContractConceptPaymentSchedule } from '../../../../models/Entities/Contracts/ContractConceptPaymentSchedule';

/** Write that is in flight for one stage while the concept is being saved. */
export type StagePendingOperation = 'add' | 'update' | 'delete';

/**
 * Editable state of a single stage row, owned by the multiselector container so
 * rows can be unmounted (collapsed groups, virtualisation) without losing edits
 * and without stalling the save, which is driven by the container itself.
 *
 * Numeric fields are kept as strings so the inputs can stay controlled without
 * fighting the intermediate values a user types ('', '0.', '-').
 */
export interface StageDraft {
  isSelected: boolean;
  isChanged: boolean;
  amount: string;
  settlementCoefficient: string;
  settlementRemoteCoefficient: string;
  settlementPhoneCoefficient: string;
  settlementFollowUpCoefficient: string;
  settlementScreeningFailureCoefficient: string;
  inPersonPrice: string;
  remotePrice: string;
  phonePrice: string;
  followUpPrice: string;
  screeningFailurePrice: string;
}

/** Names of the draft fields that are edited through a numeric input. */
export type StageDraftNumericField = Exclude<keyof StageDraft, 'isSelected' | 'isChanged'>;

/**
 * Seeds a draft from the stage's saved payment schedule. A stage is considered
 * selected when it already has one.
 */
export const buildStageDraft = (schedule: ContractConceptPaymentSchedule | undefined): StageDraft => ({
  isSelected: schedule !== undefined,
  isChanged: false,
  amount: String(schedule?.amountExpected || 1),
  settlementCoefficient: String(schedule?.settlementCoefficient ?? 1),
  settlementRemoteCoefficient: String(schedule?.settlementRemoteCoefficient || 1),
  settlementPhoneCoefficient: String(schedule?.settlementPhoneCoefficient || 1),
  settlementFollowUpCoefficient: String(schedule?.settlementFollowUpCoefficient || 1),
  settlementScreeningFailureCoefficient: String(schedule?.settlementScreeningFailureCoefficient || 1),
  inPersonPrice: String(schedule?.price || 0),
  remotePrice: String(schedule?.remotePrice || 0),
  phonePrice: String(schedule?.phonePrice || 0),
  followUpPrice: String(schedule?.followUpPrice || 0),
  screeningFailurePrice: String(schedule?.screeningFailurePrice || 0),
});

/** Reads a draft field as a number, falling back to 0 for empty or partial input. */
export const draftValueToNumber = (value: string): number => {
  const parsed = Number.parseFloat(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};
